import React from 'react'

import { SpacedGroup } from '@guidde-co/shared.spaced-group'

import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import makeStyles from '@material-ui/styles/makeStyles'

import IconRating from './icon-rating.svg'
import ViewsIcon from './icon-eye.svg'

const formatDate = (seconds: number) => {
    return new Date(seconds * 1000).toString().substring(4, 16)
}

const minimalRatingToShow = 50

const useStyles = makeStyles({
    basicMetadata: {
        padding: '8px 0',
        borderBottom: '1px solid #E0E0E0',
        fontSize: '12px'
    },
    avatar: {
        width: '24px',
        height: '24px'
    },
    viewsIcon: {
        width: '24px',
        height: '24px',
        marginLeft: '12px',
        marginRight: '4px !important'
    },
    userName: {
        fontWeight: 'bold',
        fontSize: '12px',
        wordBreak: 'break-word',
        color: '#000'
    },
    ratingIcon: {
        marginBottom: '-2px',
        marginRight: '-4px'
    },
    dataWrapper: {
        gap: 8
    },
    viewsWrapper: {
        gap: 16,
        color: '#828282'
    }
})

export type BasicMetadataProps = {
    hideTooltip?: boolean
    /**
     * Playbook data
     */
    playbook: {
        username: string
        uploadedBy: {
            displayName: string
            email: string
        }
        userImage?: string
        uploadedAt: number
        views: number
        rating: number
    }
}

export const BasicMetadata = ({
    playbook,
    hideTooltip = false
}: BasicMetadataProps) => {
    const classes = useStyles()

    const isEmpty =
        !playbook.uploadedBy?.displayName && !playbook.uploadedBy?.email
    return (
        <SpacedGroup spacing={0.5}>
            {playbook.username?.slice(0, 1).toUpperCase() && (
                <Tooltip
                    title={
                        hideTooltip || isEmpty ? null : (
                            <>
                                {playbook.uploadedBy?.displayName}
                                <br />
                                {playbook.uploadedBy?.email}
                            </>
                        )
                    }
                >
                    <Avatar
                        className={classes.avatar}
                        src={playbook?.userImage}
                        data-cy="user-image"
                    >
                        {playbook.username?.slice(0, 1).toUpperCase()}
                    </Avatar>
                </Tooltip>
            )}
            <Typography
                variant="body2"
                className={classes.userName}
                style={{
                    marginLeft: '8px'
                }}
                data-cy="username"
            >
                {playbook.username}
            </Typography>
            <Typography
                variant="body2"
                style={{
                    fontSize: '8px'
                }}
            >
                &#9679;
            </Typography>
            <Typography variant="body2" data-cy="uploaded-at-time">
                {formatDate(playbook.uploadedAt)}
            </Typography>
            <Box
                display="flex"
                alignItems="center"
                className={classes.viewsWrapper}
            >
                {playbook.views !== 0 && (
                    <SpacedGroup spacing={1}>
                        <img
                            src={ViewsIcon}
                            alt="views"
                            className={classes.viewsIcon}
                            data-cy="views-icon"
                        />
                        <Typography variant="body2">
                            {playbook.views}
                        </Typography>
                    </SpacedGroup>
                )}
                {playbook.rating >= minimalRatingToShow && (
                    <SpacedGroup spacing={1} mb={0.2}>
                        <img
                            src={IconRating}
                            alt="rating"
                            className={classes.ratingIcon}
                            data-cy="rating-icon"
                        />
                        <Typography variant="body2">
                            {playbook.rating}%
                        </Typography>
                    </SpacedGroup>
                )}
            </Box>
        </SpacedGroup>
    )
}
